<template>
  <div>
    <v-row>
      <v-col v-if="!isOrganizationManager" cols="12" md>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="organization"
          :items="organizations"
          item-value="_id"
          flat
          solo
          :label="$t('user.registration.organization')"
          class="rounded-lg"
          clearable
          :search-input.sync="orgSearch"
          :filter="localizedFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.name) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.name) }}
            </div>
          </template>
          <template v-slot:append-item>
            <div v-intersect="onOrgIntersect" v-if="hasMoreOrgs" class="text-center pa-2">
              <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col v-if="showTrainer" cols="12" sm="6" md>
        <TrainersDropDown v-model="trainer" :disabled="loading" />
      </v-col>

      <v-col cols="12" sm="6" md>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="category"
          :items="categories"
          item-value="_id"
          flat
          solo
          :label="$t('trainingCategory.category')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.name) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.name) }}
            </div>
          </template>
          <template v-slot:append-item>
            <div v-intersect="onCategoryIntersect" v-if="hasMoreCategories" class="text-center pa-2">
              <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="training"
          :items="trainings"
          item-value="_id"
          flat
          solo
          :label="$t('user.registration.training')"
          class="rounded-lg"
          clearable
          :search-input.sync="trainingSearch"
          :filter="localizedFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.title) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.title) }}
            </div>
          </template>
          <template v-slot:append-item>
            <div v-intersect="onTrainingIntersect" v-if="hasMoreTrainings" class="text-center pa-2">
              <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TrainersDropDown from "@/app/user-module/components/TrainersDropDown";

export default {
  components: { TrainersDropDown },
  data() {
    return {
      organization: null,
      trainer: null,
      training: null,
      category: null,
      organizations: [],
      trainings: [],
      categories: [],

      orgSearch: null,
      debouncedOrgSearch: null,
      orgSkip: 0,
      orgTotal: 0,
      
      trainingSearch: null,
      debouncedTrainingSearch: null,
      trainingSkip: 0,
      trainingTotal: 0,

      categorySkip: 0,
      categoryTotal: 0,

      limit: 50,
    };
  },
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
    showTrainer: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isOrganizationManager() {
      return (
        this.$store.state.authenticatedUser?.isOrganizationManager ?? false
      );
    },
    hasMoreOrgs() {
      return this.organizations.length < this.orgTotal;
    },
    hasMoreTrainings() {
      return this.trainings.length < this.trainingTotal;
    },
    hasMoreCategories() {
      return this.categories.length < this.categoryTotal;
    },
  },
  watch: {
    organization() {
      this.$emit("filter", "organization", this.organization);
      if (this.organization) this.employee = null;
    },
    trainer() {
      this.$emit("filter", "trainer", this.trainer);
    },
    training() {
      this.$emit("filter", "training", this.training);
    },
    category() {
      this.$emit("filter", "category", this.category);
    },
    orgSearch(val) {
      if (this.orgTimeout) clearTimeout(this.orgTimeout);
      this.orgTimeout = setTimeout(() => {
        if (this.debouncedOrgSearch !== val) {
          this.orgSkip = 0;
          this.debouncedOrgSearch = val;
        }
      }, 500);
    },
    trainingSearch(val) {
      if (this.trainingTimeout) clearTimeout(this.trainingTimeout);
      this.trainingTimeout = setTimeout(() => {
        if (this.debouncedTrainingSearch !== val) {
          this.trainingSkip = 0;
          this.debouncedTrainingSearch = val;
        }
      }, 500);
    },
  },
  apollo: {
    getTrainings() {
      return {
        query: require("../../course-module/api/query/getTrainings.gql"),
        variables() {
          return {
            limit: this.limit,
            skip: this.trainingSkip,
            filterInput: { search: this.debouncedTrainingSearch || null },
          };
        },
        result({ data }) {
          if (data?.getTrainings) {
            if (this.trainingSkip === 0) {
              this.trainings = data.getTrainings.trainings;
            } else {
              const existingIds = new Set(this.trainings.map(e => e._id));
              const newItems = data.getTrainings.trainings.filter(e => !existingIds.has(e._id));
              this.trainings = [...this.trainings, ...newItems];
            }
            this.trainingTotal = data.getTrainings.totalCount;
          }
        },
      };
    },
    getOrganizations() {
      return {
        query: require("../../organization-module/api/query/getOrganizations.gql"),
        variables() {
          return { 
            limit: this.limit, 
            skip: this.orgSkip, 
            filterInput: { search: this.debouncedOrgSearch || null } 
          };
        },
        result({ data }) {
          if (data?.getOrganizations) {
            if (this.orgSkip === 0) {
              this.organizations = data.getOrganizations.organizations;
            } else {
              const existingIds = new Set(this.organizations.map(e => e._id));
              const newItems = data.getOrganizations.organizations.filter(e => !existingIds.has(e._id));
              this.organizations = [...this.organizations, ...newItems];
            }
            this.orgTotal = data.getOrganizations.totalCount;
          }
        },
      };
    },
    getTrainingCategories() {
      return {
        query: require("../../category-module/api/query/getTrainingCategories.gql"),
        variables() {
          return {
            limit: this.limit,
            skip: this.categorySkip,
          };
        },
        result({ data }) {
          if (data?.getTrainingCategories) {
            if (this.categorySkip === 0) {
              this.categories = data.getTrainingCategories.trainingCategories;
            } else {
              const existingIds = new Set(this.categories.map(e => e._id));
              const newItems = data.getTrainingCategories.trainingCategories.filter(e => !existingIds.has(e._id));
              this.categories = [...this.categories, ...newItems];
            }
            this.categoryTotal = data.getTrainingCategories.totalCount;
          }
        },
      };
    },
  },
  methods: {
    localizedFilter(item, queryText, itemText) {
      const text = this.getLocalisedValue(item.name || item.title) || "";
      return text.toLowerCase().includes(queryText.toLowerCase());
    },
    onOrgIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.hasMoreOrgs && !this.$apollo.queries.getOrganizations.loading) {
        this.orgSkip += this.limit;
        this.$apollo.queries.getOrganizations.fetchMore({
          variables: {
            skip: this.orgSkip,
            limit: this.limit,
            filterInput: { search: this.orgSearch || null },
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              getOrganizations: {
                __typename: previousResult.getOrganizations.__typename,
                totalCount: fetchMoreResult.getOrganizations.totalCount,
                organizations: [
                  ...previousResult.getOrganizations.organizations,
                  ...fetchMoreResult.getOrganizations.organizations,
                ],
              },
            };
          },
        });
      }
    },
    onTrainingIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.hasMoreTrainings && !this.$apollo.queries.getTrainings.loading) {
        this.trainingSkip += this.limit;
        this.$apollo.queries.getTrainings.fetchMore({
          variables: {
            skip: this.trainingSkip,
            limit: this.limit,
            filterInput: { search: this.trainingSearch || null },
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              getTrainings: {
                __typename: previousResult.getTrainings.__typename,
                totalCount: fetchMoreResult.getTrainings.totalCount,
                trainings: [
                  ...previousResult.getTrainings.trainings,
                  ...fetchMoreResult.getTrainings.trainings,
                ],
              },
            };
          },
        });
      }
    },
    onCategoryIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.hasMoreCategories && !this.$apollo.queries.getTrainingCategories.loading) {
        this.categorySkip += this.limit;
        this.$apollo.queries.getTrainingCategories.fetchMore({
          variables: {
            skip: this.categorySkip,
            limit: this.limit,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              getTrainingCategories: {
                __typename: previousResult.getTrainingCategories.__typename,
                totalCount: fetchMoreResult.getTrainingCategories.totalCount,
                trainingCategories: [
                  ...previousResult.getTrainingCategories.trainingCategories,
                  ...fetchMoreResult.getTrainingCategories.trainingCategories,
                ],
              },
            };
          },
        });
      }
    },
  },
};
</script>
